import { TournamentUser } from 'modules/teams/domain/interfaces/tournament-user';
import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { USER_DEFAULT_AVATAR } from 'shared/constants/links';
import { getBirthDay, getFullName } from 'shared/utils';
import { UDSelect } from 'ud-form/components';
import { UDEntityRow } from 'ud-ui/components';
import UDButton from 'ud-ui/components/button';

/** Роли, которые можно назначить участнику. Совпадает с ключами `player.role`. */
const ROLES = ['STAFF', 'CAPTAIN', 'FAN'];

interface ParticipantRowProps {
  participant: TournamentUser;
  /** Порядковый номер строки, уже готовый к показу. */
  index: number;
  /** Идёт запрос по этой строке: селект и кнопка заблокированы только у неё. */
  isLoading: boolean;
  onChangeRole: (participant: TournamentUser, role: string) => void;
  onKick: (participant: TournamentUser) => void;
}

/**
 * Строка участника команды: номер, фото, ФИО, дата рождения, селект роли и
 * «Исключить».
 *
 * Формат даты рождения перенесён как есть (`getBirthDay`, возраст в скобках): у
 * состава команды он другой, сведение форматов — отдельная задача.
 */
const ParticipantRow = ({ participant, index, isLoading, onChangeRole, onKick }: ParticipantRowProps) => {
  const { t } = useTranslation();

  const handleKick = useCallback(() => onKick(participant), [onKick, participant]);

  return (
    <UDEntityRow
      index={index}
      // Фото приходит пустой строкой, а не `null`, поэтому `||`, а не `??`.
      avatar={participant.photo?.externalUrl || USER_DEFAULT_AVATAR}
      title={getFullName(participant)}
      subtitle={getBirthDay({ birthDate: new Date(participant?.birthDate ?? '') })}
      actions={
        <>
          <UDSelect
            divProps={{ style: { width: 144 } }}
            isDisabled={isLoading}
            isLoading={isLoading}
            options={ROLES.map((role) => ({
              label: t(`player.role.${role}`),
              value: role,
            }))}
            value={{
              label: t(`player.role.${participant.role}`),
              value: participant.role,
            }}
            onChange={(value) => onChangeRole(participant, value?.value ?? '')}
          />

          <UDButton style={{ width: 144 }} variant="secondary" disabled={isLoading} loading={isLoading} onClick={handleKick}>
            {t('participant.remove')}
          </UDButton>
        </>
      }
    />
  );
};

export default React.memo(ParticipantRow);
